import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/requests';
import log from '../../logger';

// Fetch occasions
export const fetchOccasions = createAsyncThunk(
  'occasion/fetchOccasions',
  async ({ page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Fetching occasions with page:', page, 'and limit:', limit);
      const response = await api.occasions.getAll({ page, limit, filters });
      if (response.data.status) {
        log.info('Occasions fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch occasions:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching occasions:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching occasions');
    }
  }
);

// Search occasions with keyword
export const searchOccasions = createAsyncThunk(
  'occasion/searchOccasions',
  async ({ keyword, page = 1, limit = 10, filters = {} } = {}, { rejectWithValue }) => {
    try {
      log.info('Searching occasions with keyword:', keyword);
      const response = await api.occasions.search({ keyword, page, limit, ...filters });
      if (response.data.status) {
        log.info('Occasions search completed successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to search occasions:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error searching occasions:', error);
      return rejectWithValue(error.response?.data?.message || 'Error searching occasions');
    }
  }
);

// Create occasion
export const createOccasion = createAsyncThunk(
  'occasion/createOccasion',
  async (formData, { rejectWithValue }) => {
    try {
      log.info('Creating occasion with data:', formData);
      const response = await api.occasions.create(formData);
      if (response.data.status) {
        log.info('Occasion created successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to create occasion:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error creating occasion:', error);
      return rejectWithValue(error.response?.data?.message || 'Failed to create occasion');
    }
  }
);

// Update occasion
export const updateOccasion = createAsyncThunk(
  'occasion/updateOccasion',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      log.info('Updating occasion with ID:', id, 'and data:', formData);
      const response = await api.occasions.update(id, formData);
      if (response.data.status) {
        log.info('Occasion updated successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to update occasion:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error updating occasion:', error);
      return rejectWithValue(error.response?.data?.message || 'Error updating occasion');
    }
  }
);

// Delete occasion
export const deleteOccasion = createAsyncThunk(
  'occasion/deleteOccasion',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Deleting occasion with ID:', id);
      const response = await api.occasions.delete(id);
      if (response.data.status) {
        log.info('Occasion deleted successfully:', id);
        return id;
      } else {
        log.warn('Failed to delete occasion:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error deleting occasion:', error);
      return rejectWithValue(error.response?.data?.message || 'Error deleting occasion');
    }
  }
);

// Bulk delete occasions
export const bulkDeleteOccasions = createAsyncThunk(
  'occasion/bulkDeleteOccasions',
  async (ids, { rejectWithValue }) => {
    try {
      await Promise.all(ids.map(id => api.occasions.delete(id)));
      return ids;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error deleting occasions');
    }
  }
);

// Bulk activate/deactivate occasions
export const bulkUpdateOccasionStatus = createAsyncThunk(
  'occasion/bulkUpdateStatus',
  async ({ ids, status }, { rejectWithValue }) => {
    try {
      await api.occasions.bulkUpdateStatus(ids, status);
      return { ids, status };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error updating occasion status');
    }
  }
);

// Fetch a single occasion by ID
export const fetchOccasionById = createAsyncThunk(
  'occasion/fetchOccasionById',
  async (id, { rejectWithValue }) => {
    try {
      log.info('Fetching occasion with ID:', id);
      const response = await api.occasions.getById(id);
      if (response.data.status) {
        log.info('Occasion fetched successfully:', response.data.data);
        return response.data.data;
      } else {
        log.warn('Failed to fetch occasion:', response.data.message);
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      log.error('Error fetching occasion:', error);
      return rejectWithValue(error.response?.data?.message || 'Error fetching occasion');
    }
  }
);

const initialState = {
  items: [],
  total: 0,
  page: 1,
  pages: 0,
  limit: 10,
  loading: false,
  error: null
};

const occasionSlice = createSlice({
  name: 'occasion',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch occasions
      .addCase(fetchOccasions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOccasions.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.occasions;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(fetchOccasions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Search occasions
      .addCase(searchOccasions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchOccasions.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = action.payload.occasions;
        state.total = parseInt(action.payload.total);
        state.page = parseInt(action.payload.page);
        state.pages = parseInt(action.payload.pages);
        state.limit = parseInt(action.payload.limit);
      })
      .addCase(searchOccasions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create occasion
      .addCase(createOccasion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createOccasion.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items.push(action.payload);
      })
      .addCase(createOccasion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update occasion
      .addCase(updateOccasion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOccasion.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      .addCase(updateOccasion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete occasion
      .addCase(deleteOccasion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteOccasion.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = state.items.filter(item => item._id !== action.payload);
      })
      .addCase(deleteOccasion.rejected, (state) => {
        state.loading = false;
      })
      // Bulk delete occasions
      .addCase(bulkDeleteOccasions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkDeleteOccasions.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(bulkDeleteOccasions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(bulkUpdateOccasionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bulkUpdateOccasionStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { ids, status } = action.payload;
        state.items = state.items.map(item => ids.includes(item._id) ? { ...item, status } : item);
      })
      .addCase(bulkUpdateOccasionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch a single occasion by ID
      .addCase(fetchOccasionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOccasionById.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.items = [action.payload]; // Assuming we want to replace the current items with the fetched one
      })
      .addCase(fetchOccasionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default occasionSlice.reducer; 